import {
  Artwork,
  Player,
  PlayerConnectionStatus,
  PlayerGameStatus,
  QuestionType,
  Round,
  SessionData,
  SessionStatus
} from '@/API';
import { PlayerSessionContext } from '@/contexts/PlayerSessionContext';
import { Reaction as ReactionOption } from '@/utils/reactions';
import { ReactNode, useCallback, useMemo, useState } from 'react';
import { Reaction } from '@/components/Controls/ReactionsBar';

/**
 * A drop-in replacement for PlayerSessionControlProvider that keeps all state in
 * memory.
 *
 * Purpose: exercise the real gameplay UI with no Scribl account, no API, and no
 * database, so the Zoom integration can be tested independently of backend access.
 * It satisfies the same PlayerSessionContext contract, so PlayerCanvas and
 * PlayerSpotlight are the genuine components rather than mock-ups.
 *
 * Nothing here is used by the real game: the live provider remains untouched.
 */

const YOU_ID = 'demo-you';
const SHARER_ID = 'demo-sharer';

const player = (id: string, name: string, gameStatus: PlayerGameStatus): Player =>
  ({
    __typename: 'Player',
    id,
    name,
    gameStatus,
    connectionStatus: PlayerConnectionStatus.CONNECTED
  }) as Player;

export default function DemoSessionProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<SessionStatus>(SessionStatus.drawing);
  const [artwork, setArtwork] = useState<Artwork[]>([]);
  const [reactions, setReactions] = useState<Reaction[]>([]);

  // While drawing you are the only participant. Once you submit, a stand-in
  // participant "shares" your drawing back to you so the spotlight and reaction
  // steps can be exercised solo.
  const players = useMemo(() => {
    if (status === SessionStatus.drawing) {
      return [player(YOU_ID, 'You', PlayerGameStatus.DRAWING)];
    }

    // The stand-in is given a person's name so the spotlight copy reads naturally
    // ("Alex is sharing!") and matches what a real multiplayer round looks like.
    // The artwork shown is still the drawing you just submitted.
    return [
      player(YOU_ID, 'You', PlayerGameStatus.SUBMITTED),
      player(SHARER_ID, 'Alex', PlayerGameStatus.SHARING)
    ];
  }, [status]);

  const currentRound = useMemo(
    () =>
      ({
        __typename: 'Round',
        id: 'demo-round',
        index: 0,
        isCurrent: true,
        isLast: true,
        // Left empty on purpose: it would trigger a prompt fetch. Demo screens pass
        // the prompt text directly instead.
        promptId: '',
        artwork
      }) as unknown as Round,
    [artwork]
  );

  const session = useMemo(
    () =>
      ({
        __typename: 'SessionData',
        status,
        players,
        rounds: [currentRound],
        questionType: QuestionType.CONNECTED
      }) as unknown as SessionData,
    [status, players, currentRound]
  );

  const submitDrawing = useCallback(async (drawing: string) => {
    setArtwork([
      {
        __typename: 'Artwork',
        id: 'demo-card',
        playerId: SHARER_ID,
        drawing
      } as Artwork
    ]);
    setStatus(SessionStatus.sharing);
  }, []);

  const reactToArtwork = useCallback(async (reaction: ReactionOption) => {
    setReactions((current) => [
      ...current,
      {
        playerId: YOU_ID,
        playerCardId: 'demo-card',
        reactionType: reaction.value,
        displayed: false
      } as unknown as Reaction
    ]);
  }, []);

  const value = useMemo(
    () => ({
      state: 'connected' as const,
      currentRound,
      artwork,
      player: players.find((p) => p.id === YOU_ID),
      players,
      reactions,
      session,
      submitDrawing,
      reactToArtwork,
      getArtworkReactions: async () => {},
      setupPlayer: async () => {},
      updatePlayerSentiment: async () => {},
      updatePlayerQuestion: async () => {}
    }),
    [currentRound, artwork, players, reactions, session, submitDrawing, reactToArtwork]
  );

  return <PlayerSessionContext.Provider value={value}>{children}</PlayerSessionContext.Provider>;
}
